import { BloodGlucoseObservation } from "@/model/BloodGlucoseObservation";
import { BloodGlucoseReading } from "@/model/BloodGlucoseReading";
import cn from "@/util/cn";
import { DateTimeFormatter, Instant, LocalDate, LocalTime, ZoneId } from "@js-joda/core";
import { Locale } from "@js-joda/locale";
import first from "lodash/first";
import { ComponentPropsWithoutRef, Fragment, useEffect, useRef, useState } from "react";
import { Cell, HeaderCell } from "../../Cell";
import MeasurementLogCell from "./MeasurementCell";

const timeBuckets = [
  "00:00/04:00",
  "04:00/08:00",
  "08:00/12:00",
  "12:00/16:00",
  "16:00/20:00",
  "20:00/23:59:59.999999999",
] as const;

type TimeBuckets = typeof timeBuckets;
type TimeBucket = TimeBuckets[number];

type MeasurementLogTableProps = ComponentPropsWithoutRef<"div"> & {
  dates: LocalDate[];
  observations: BloodGlucoseObservation[];
  lastReviewTime: Instant;
  selectedObservation: BloodGlucoseObservation | null;
  onObservationSelected: (observation: BloodGlucoseObservation) => void;
};

const MeasurementLogTable = ({
  dates,
  observations,
  lastReviewTime,
  selectedObservation,
  onObservationSelected,
  className,
  ...otherProps
}: MeasurementLogTableProps) => {
  const isSelectedObservation = (observation: BloodGlucoseObservation | undefined) =>
    selectedObservation !== null && observation?.id === selectedObservation.id;

  const timeSpanRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldWrapAll, setShouldWrapAll] = useState(false);

  // This is here to check if the first column is wrapping.
  // If it is, we need to wrap all the columns for consistency.
  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver(() => {
      const checkFirstColumnWrapping = () => {
        const firstSpan = timeSpanRefs.current[0];

        if (firstSpan) {
          const lineHeight = parseInt(getComputedStyle(firstSpan).lineHeight);
          const isWrapped = firstSpan.offsetHeight > lineHeight + 2;

          if (shouldWrapAll !== isWrapped) {
            setShouldWrapAll(isWrapped);
          }
        }
      };

      requestAnimationFrame(checkFirstColumnWrapping);
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [shouldWrapAll]);

  return (
    <div className={className} {...otherProps}>
      <div
        ref={containerRef}
        className="grid min-w-0 grid-cols-[min-content_repeat(6,minmax(0,1fr))] gap-px ps-px"
      >
        <Cell />
        {timeBuckets.map((timeBucket, index) => {
          const formattedTimeBucket = formatTimeBucket(timeBucket);
          return (
            <HeaderCell
              key={`header-${timeBucket}`}
              title={formattedTimeBucket[0] + " - " + formattedTimeBucket[1]}
            >
              <span
                ref={(el) => {
                  timeSpanRefs.current[index] = el;
                  return undefined;
                }}
                className="h-fit text-sm leading-6"
              >
                {formattedTimeBucket[0]}
                <span> - </span>
                <span className={cn("whitespace-nowrap", shouldWrapAll && index !== 0 && "block")}>
                  {formattedTimeBucket[1]}
                </span>
              </span>
            </HeaderCell>
          );
        })}
        {dates.map((date, i) => {
          const dateString = dayMonthFormatter.format(date);
          const dayOfWeekString = dayOfWeekFormatter.format(date);
          return (
            <Fragment key={`reading-${i}`}>
              <HeaderCell className="justify-start">
                <span className="inline-block" style={{ minWidth: `${dateString.length}ch` }}>
                  {dateString}
                </span>
                {dayOfWeekString}
              </HeaderCell>
              {timeBuckets.map((timeBucket) => {
                const [bucketStart] = parseTimeBucket(timeBucket);
                const startInstant = date.atTime(bucketStart).atZone(ZoneId.SYSTEM).toInstant();
                const _observations = observationsForCell({
                  observations,
                  date,
                  timeBucket,
                });
                const isNew = _observations.some((observation) =>
                  observation.dateTime.toInstant().isAfter(lastReviewTime)
                );
                const isEmpty = _observations.length === 0;
                const isDisabled = isEmpty && startInstant.isAfter(Instant.now());
                const status = isDisabled ? "disabled" : isNew ? "new" : "default";
                const firstObservationInCell = first(_observations);
                return (
                  <MeasurementLogCell
                    key={`reading-cell-${date}-${timeBucket}`}
                    readings={_observations.map((observation) => observation.reading)}
                    status={status}
                    onClick={() => {
                      if (!isEmpty) {
                        onObservationSelected(firstObservationInCell!);
                      }
                    }}
                    className={cn(
                      isEmpty ? "cursor-default" : "cursor-pointer",
                      isEmpty
                        ? ""
                        : isSelectedObservation(firstObservationInCell)
                          ? "ring-2 ring-inset ring-blue-400 print:ring-transparent"
                          : "ring-blue-400 hover:ring-2 hover:ring-inset hover:ring-blue-400/50 print:ring-transparent print:hover:ring-transparent"
                    )}
                  />
                );
              })}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

const observationsForCell = ({
  observations,
  date,
  timeBucket,
}: {
  observations: BloodGlucoseObservation[];
  date: LocalDate;
  timeBucket: TimeBucket;
}): (BloodGlucoseObservation & { reading: BloodGlucoseReading })[] =>
  observations
    .filter((observation) => Boolean(observation.reading))
    .map((observation) => ({ ...observation, reading: observation.reading! }))
    .filter((observation) => {
      const readingDateTime = observation.reading.dateTime;
      const readingDate = readingDateTime.toLocalDate();
      const readingTime = readingDateTime.toLocalTime();
      return readingDate.equals(date) && contains(timeBucket, readingTime);
    });

const contains = (timeBucket: TimeBucket, time: LocalTime) => {
  const [bucketStart, bucketEnd] = parseTimeBucket(timeBucket);
  return time.equals(bucketStart) || (time.isAfter(bucketStart) && time.isBefore(bucketEnd));
};

const parseTimeBucket = (timeBucket: TimeBucket) =>
  timeBucket.split("/").map((it) => LocalTime.parse(it));

const formatTimeBucket = (timeBucket: TimeBucket) => {
  const [startTime, endTime] = parseTimeBucket(timeBucket);
  const formattedStart = timeFormatter.format(startTime);
  const formattedEnd = timeFormatter.format(endTime);
  return [formattedStart, formattedEnd];
};

const dayOfWeekFormatter = DateTimeFormatter.ofPattern("EEEE").withLocale(Locale.US);
const dayMonthFormatter = DateTimeFormatter.ofPattern("MM/dd").withLocale(Locale.US);
const timeFormatter = DateTimeFormatter.ofPattern("h:mm a").withLocale(Locale.US);

export default MeasurementLogTable;
